import { CheckCircle, Close, CloudUpload, Upload } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useProductReadyUpload } from "../../hooks/useProductReadyUpload";
import type { Photo } from "../../types/api";

interface ProductReadyUploadProps {
  photos: Photo[];
  campusId: string;
  childId: string;
  onUploadComplete: () => void;
}

const ProductReadyUpload: React.FC<ProductReadyUploadProps> = ({
  photos,
  campusId,
  childId,
  onUploadComplete,
}) => {
  const {
    files,
    matchedPhotos,
    unmatchedFiles,
    uploading,
    progress,
    overallProgress,
    error,
    success,
    onDrop,
    matchFile,
    removeFile,
    uploadAll,
    setError,
    setSuccess,
  } = useProductReadyUpload(photos, campusId, childId);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
    },
    disabled: uploading,
  });

  const handleUploadComplete = useCallback(async () => {
    await uploadAll();
    onUploadComplete();
  }, [uploadAll, onUploadComplete]);

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Upload sx={{ fontSize: 32, color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload Product-Ready Photos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload final high-resolution edited photos for this order
            </Typography>
          </Box>
        </Box>

        {/* Overall Progress Display */}
        {uploading && overallProgress.total > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Overall Progress
              </Typography>
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                {overallProgress.current}/{overallProgress.total} files
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                overallProgress.total > 0
                  ? (overallProgress.current / overallProgress.total) * 100
                  : 0
              }
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: "rgba(0, 0, 0, 0.12)",
              }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Helper Text */}
        <Alert severity="info" icon={false} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            💡 Quick Tip: Auto-Match by Index
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Rename your final photos to match the photo index (e.g., "1.jpg" for
            Photo #1, "2.jpg" for Photo #2) and they will be automatically
            matched when you upload them.
          </Typography>
        </Alert>

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "grey.300",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            bgcolor: isDragActive ? "primary.light" : "grey.50",
            cursor: uploading ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            mb: 3,
            "&:hover": {
              borderColor: uploading ? "grey.300" : "primary.main",
              bgcolor: uploading ? "grey.50" : "primary.light",
              "& .MuiTypography-root": {
                color: uploading ? "text.secondary" : "#DDD",
              },
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {isDragActive
              ? "Drop files here..."
              : "Drag & drop final photos here"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              transition: "color 0.2s",
            }}
          >
            or click to browse files
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              color: "text.secondary",
              transition: "color 0.2s",
            }}
          >
            Accepted formats: JPEG, PNG, GIF (max 10MB)
          </Typography>
        </Box>

        {/* File List */}
        {files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Files ({files.length})
            </Typography>
            <Grid container spacing={2}>
              {files.map((file) => {
                const matchedPhoto = matchedPhotos.get(file.id);
                const fileProgress = progress[file.id];

                return (
                  <Grid size={{ xs: 12 }} key={file.id}>
                    <Card variant="outlined">
                      <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                        {/* Preview Thumbnail */}
                        <Box
                          component="img"
                          src={file.preview}
                          alt={file.file.name}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 1,
                            mr: 2,
                          }}
                        />

                        {/* File Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>

                          {/* Matching Info */}
                          <Box sx={{ mt: 1 }}>
                            {matchedPhoto ? (
                              <Chip
                                size="small"
                                icon={<CheckCircle />}
                                label={`Matched to Photo #${
                                  photos.indexOf(matchedPhoto) + 1
                                }`}
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Match to photo</InputLabel>
                                <Select
                                  label="Match to photo"
                                  onChange={(e) =>
                                    matchFile(file.id, e.target.value as string)
                                  }
                                  defaultValue=""
                                >
                                  {photos.map((photo, index) => (
                                    <MenuItem key={photo.id} value={photo.id}>
                                      Photo #{index + 1}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </Box>

                          {/* Progress Bar */}
                          {fileProgress && fileProgress.uploading && (
                            <LinearProgress
                              variant="indeterminate"
                              sx={{ mt: 1 }}
                            />
                          )}
                          {fileProgress && fileProgress.completed && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              <CheckCircle
                                sx={{
                                  fontSize: 16,
                                  color: "success.main",
                                  mr: 0.5,
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="success.main"
                              >
                                Uploaded successfully
                              </Typography>
                            </Box>
                          )}
                          {fileProgress && fileProgress.error && (
                            <Typography variant="caption" color="error">
                              Upload failed
                            </Typography>
                          )}
                        </Box>

                        {/* Remove Button */}
                        {!uploading && !file.uploaded && (
                          <IconButton
                            size="small"
                            onClick={() => removeFile(file.id)}
                          >
                            <Close />
                          </IconButton>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Upload Button */}
            <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleUploadComplete}
                disabled={uploading || files.length === 0}
                startIcon={<Upload />}
              >
                {uploading ? "Uploading..." : `Upload ${files.length} File(s)`}
              </Button>

              {unmatchedFiles.length > 0 && (
                <Alert severity="warning" sx={{ flex: 1 }}>
                  {unmatchedFiles.length} unmatched file(s). Please match them
                  manually.
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductReadyUpload;
